import type { ComponentType, FC, ReactNode } from "react";
import { CircularProgress } from "@privacy/ui";
import { TextField, Stack, IconButton, Box, Tooltip, Typography, type SxProps, type Theme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import FormControl from "@mui/material/FormControl";
import { visuallyHidden } from "@mui/utils";
import { TextFieldActionButtons, type TextFieldActionButtonsProps } from "../../../shared/TextFieldActionButtons";
import { useDynamicFormState } from "../../DynamicFormProvider";
import type { InputProps } from "../../DynamicInput";
import { ErrorMessage } from "../ErrorMessage";
import { isAnswerRequestEmpty } from "../../shared/isAnswerRequestEmpty";
import { EmptyPlaceholderText } from "../EmptyPlaceholderText";
import type { IconProps } from "../../../shared/icons";
import { CheckIcon, XIcon } from "../../../shared/icons";
import type { TranslationKey } from "../../../translations/TranslationProvider";
import { useInlineInputState } from "./InlineInputState";

type TextProps = InputProps<string> & {
  validation?: UseTextInputProps["validation"] & { errorMessageKey: TranslationKey };
  Icon?: ComponentType<IconProps>;
  numberOfRows?: number;
  renderNonEditingInputContent?: (value: string) => ReactNode;
};

export const Text: FC<TextProps> = ({
  validation,
  readonly,
  Icon,
  numberOfRows = 1,
  renderNonEditingInputContent,
  questionCard,
  onSave,
}) => {
  const form = useFormContext();
  const { navigationBlockState, mandatoryQuestionErrorMessage } = useDynamicFormState();
  const { t } = useTranslation();

  return (
    <StyledInlineTextInput
      form={form}
      numberOfRows={numberOfRows}
      validation={validation}
      Icon={Icon}
      id={questionCard.id}
      isReadOnly={readonly}
      renderNonEditingInputContent={renderNonEditingInputContent}
      onSave={onSave}
      renderErrorMessage={({ isValidationError, isSaveError, isSaving, isDirty, field: { value } }) => {
        return (
          <>
            {validation && isValidationError && <ErrorMessage errorMessage={t(validation.errorMessageKey)} />}
            {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(value) && (
              <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
            )}

            {(navigationBlockState === "cancelled" || navigationBlockState === "blocked") && isDirty && (
              <ErrorMessage variant="warning" errorMessage={t("DynamicForm.TextInput.UnsavedChangesWarning")} />
            )}

            {!isSaving && isSaveError && <ErrorMessage />}
          </>
        );
      }}
    />
  );
};

export const TextActionButtonsSingleLine: FC<TextFieldActionButtonsProps> = props => {
  const { onSave, onCancel, isSaving, isDirty, isEditing } = props;
  const { t } = useTranslation();

  return (
    <Stack
      sx={{
        position: "absolute",
        display: isEditing ? "flex" : "none",
        alignItems: "center",
        width: "fit-content",
        justifyContent: "flex-end",
        gap: 0.5,
        p: 0.5,
        bottom: 0,
        right: 0,
        borderBottomLeftRadius: theme => theme.shape.borderRadius,
        borderBottomRightRadius: theme => theme.shape.borderRadius,
        backgroundColor: theme => theme.palette.background.paper,
        transform: "translateY(100%)",
        boxShadow: theme => `0 2px 4px 0 ${theme.palette.grey["300"]}`,
        borderTop: "none",
        zIndex: 1,
        flexDirection: "row",
      }}
    >
      <Tooltip title={t("DynamicForm.Inline.TextInputSaveButton")}>
        <IconButton
          aria-label={t("DynamicForm.Inline.TextInputSaveButton")}
          tabIndex={isDirty ? 0 : -1}
          sx={{
            "backgroundColor": theme => theme.palette.primary.main,
            "color": theme => theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme => theme.palette.primary.light },
          }}
          type="submit"
          onClick={() => (isDirty ? onSave() : onCancel())}
          size="small"
        >
          {isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon size={16} />}
        </IconButton>
      </Tooltip>

      <Tooltip title={t("DynamicForm.Inline.TextInputCancelButton")}>
        <IconButton
          aria-label={t("DynamicForm.Inline.TextInputCancelButton")}
          tabIndex={isDirty ? 0 : -1}
          sx={{
            "backgroundColor": theme => theme.palette.grey["200"],
            "&:hover": { backgroundColor: theme => theme.palette.grey["300"] },
          }}
          onClick={onCancel}
          size="small"
        >
          <XIcon size={16} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

/** Todo: This component is awful, it is used to display whatever content in the text field when not in "editing mode"
 * etc a link. But it is a bad solution and should be replaced */
type TextFieldPlaceholderProps = {
  onFocus: () => void;
  value: string;
  id: string;
  disabled?: boolean;
  children: ReactNode;
  isReadOnly?: boolean;
};

const TextFieldPlaceholder = ({ onFocus, id, value, children, isReadOnly, disabled }: TextFieldPlaceholderProps) => {
  return (
    <Box sx={{ position: "relative" }}>
      <Box
        htmlFor={id}
        component="label"
        sx={[
          {
            "all": "unset",
            "display": "flex",
            "&:hover": { boxShadow: theme => `0 0 0 1px ${theme.palette.grey["300"]}` },
            "fontSize": theme => theme.typography.pxToRem(14),
            "cursor": "text",
            "padding": "7.5px 12px",
          },

          Boolean(isReadOnly) && {
            cursor: "default",
            color: "revert",
            boxShadow: "none !important",
          },
        ]}
      >
        {children}
      </Box>
      <Box
        id={id}
        disabled={disabled}
        defaultValue={value}
        onFocus={onFocus}
        component="input"
        sx={visuallyHidden}
        autoComplete="off"
        data-1p-ignore="true"
      />
    </Box>
  );
};

type UseTextInputProps = {
  validation?: { validator: (value: string) => boolean };
  id: string;
  onSave: (value: string) => Promise<unknown>;
  form: UseFormReturn<any>;
  numberOfRows: number;
  isReadOnly?: boolean;
};
export const useTextInput = ({
  validation,
  id,
  onSave,
  numberOfRows,
  form: { control, setFocus, trigger, resetField },
  isReadOnly,
}: UseTextInputProps) => {
  const {
    field: { ref: inputRef, ...field },
    fieldState: { error, isDirty },
  } = useController({
    control,
    name: id,
    rules: { validate: value => (validation ? validation.validator(value) : true) },
  });

  const [isSavedClicked, setIsSavedClicked] = useState(false);
  const { inputState, setIsEditing } = useInlineInputState(isReadOnly);

  const {
    mutate: save,
    isPending: isSaving,
    isError: isSaveError,
    reset: resetSave,
  } = useMutation({
    mutationFn: () => onSave(field.value),
    onSuccess: () => setIsEditing(false),
  });

  const isValidationError = isSavedClicked && Boolean(error);

  const handleStartEditing = () => {
    setIsEditing(true);
    setTimeout(() => setFocus(id, { shouldSelect: true }));
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSavedClicked(true);
    const isValid = await trigger(id);
    if (isValid) save();
    else setFocus(id);
  };

  const handleCancel = () => {
    resetField(id);
    resetSave();
    setIsSavedClicked(false);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isDirty && numberOfRows === 1 && event.key === "Enter") {
      event.preventDefault();
      handleSave();
    }
    if ((!isDirty || numberOfRows === 1) && event.key === "Escape") {
      event.preventDefault();
      handleCancel();
    }
  };

  return {
    isSaving,
    isSaveError,
    inputState,
    field,
    isValidationError,
    setIsEditing,

    handleStartEditing,
    handleSave,
    handleCancel,
    inputRef,
    isDirty,
    handleKeyDown,
  };
};

/** This component can also be used outside the dynamic form in order to
 *  get the same UI for "hardcoded" text inputs in the drawer.*/
type StyledInlineTextInputProps = {
  id: string;
  renderNonEditingInputContent?: (value: string) => ReactNode;
  Icon?: ComponentType<IconProps>;
  numberOfRows?: number;
  renderErrorMessage?: (args: ReturnType<typeof useTextInput>) => ReactNode;
  inputSx?: SxProps<Theme>;
} & UseTextInputProps;

export const StyledInlineTextInput = ({
  id,
  renderNonEditingInputContent,
  Icon,
  numberOfRows = 1,
  validation,
  onSave,
  form,
  renderErrorMessage,
  inputSx,
  isReadOnly,
}: StyledInlineTextInputProps) => {
  const textInput = useTextInput({ validation, id, onSave, form, numberOfRows, isReadOnly });

  const {
    handleStartEditing,
    handleCancel,
    handleSave,
    isSaving,
    inputRef,
    inputState,
    setIsEditing,
    field,
    isDirty,
    handleKeyDown,
  } = textInput;

  if (inputState !== "editing") {
    return (
      <FormControl fullWidth>
        <TextFieldPlaceholder
          disabled={isReadOnly}
          value={field.value}
          isReadOnly={isReadOnly}
          id={id}
          onFocus={handleStartEditing}
        >
          <Box>
            {renderNonEditingInputContent ? (
              renderNonEditingInputContent(field.value)
            ) : field.value ? (
              <Typography
                sx={{
                  gap: 1,
                  fontSize: theme => theme.typography.pxToRem(14),
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                  display: "flex",
                  alignItems: "center",
                  ...inputSx,
                }}
              >
                {Icon && <Icon size={16} />}
                {field.value}
              </Typography>
            ) : (
              <EmptyPlaceholderText />
            )}
          </Box>
        </TextFieldPlaceholder>
        {renderErrorMessage?.(textInput)}
      </FormControl>
    );
  }
  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        onBlur={TextFieldActionButtons.onTextFieldWrapperBlur({ isDirty, onCancel: handleCancel })}
        sx={{ position: "relative", alignSelf: "flex-start", width: "100%" }}
      >
        <FormControl fullWidth>
          <TextField
            {...field}
            value={field.value ?? ""}
            autoComplete="off"
            id={id}
            onKeyDown={handleKeyDown}
            disabled={isReadOnly}
            onFocus={() => setIsEditing(true)}
            inputRef={inputRef}
            size="small"
            multiline={numberOfRows > 1}
            minRows={numberOfRows}
            sx={{ ...(numberOfRows > 1 ? TextFieldActionButtons.TextFieldSx : undefined) }}
            slotProps={{
              htmlInput: { sx: inputSx },
              input: {
                endAdornment:
                  numberOfRows > 1 ? (
                    <TextFieldActionButtons
                      isSaving={isSaving}
                      onSave={handleSave}
                      isEditing={inputState === "editing"}
                      isDirty={isDirty}
                      onCancel={handleCancel}
                    />
                  ) : undefined,
              },
            }}
          />
        </FormControl>

        {numberOfRows === 1 && (
          <TextActionButtonsSingleLine
            isSaving={isSaving}
            onSave={handleSave}
            isEditing={inputState === "editing"}
            isDirty={isDirty}
            onCancel={handleCancel}
          />
        )}
      </Stack>
      {renderErrorMessage?.(textInput)}
    </Box>
  );
};
